import { useMemo } from "react";
import InlineEditableDetail, {
  type InlineEditorSpec,
} from "../InlineEdit/InlineEditableDetail";
import { getCustomFieldValue } from "@/utils/getFromCustom";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import type { ResolvedField } from "@/features/customField/api/useResolveCustomDefaults";
import { isTagSelector } from "@/features/customField/utils/cfSelectionMode";
import {
  isFormattedCfType,
  isValidYmd,
  resolveCfDateMax,
} from "@/features/customField/utils/cfValueTypes";
import { useRequiredFieldMark } from "@/features/leads/requirements/requiredFieldMarks";

/**
 * One custom-field row in the Preference card, editable in place.
 *
 * Mirrors what the global edit panel renders for the same field:
 *   - SELECTOR, Tag    → multi-select
 *   - SELECTOR, Normal → single select
 *   - TEXT_FIELD       → single-line
 *   - REMARK           → multi-line
 *   - NUMBERS / PERCENTAGE → single-line, digits and one point only
 *   - DATE             → calendar, never past the field's limit
 *
 * The Tag/Normal choice is the FIELD's (`CustomInput.valueTypes`), so the row
 * looks identical on the lead and on a deal. On a deal the options are still
 * narrowed to what the attached niches support (`resolvedOptions`) — that is
 * about WHICH options exist, not how many may be picked.
 */
export default function PreferenceInlineItem({
  field,
  scopes,
  resolvedOptions,
  canEdit,
  onSave,
}: {
  field: any;
  scopes: any[];
  resolvedOptions?: ResolvedField["resolvedOptions"];
  canEdit: boolean;
  onSave: (
    customInputId: number,
    patch: { optionIds?: string[]; textValue?: string },
  ) => Promise<unknown> | unknown;
}) {
  const scope = useMemo(
    () =>
      (scopes ?? []).find(
        (s: any) => Number(s?.customInputId) === Number(field?.id),
      ),
    [scopes, field],
  );
  // Stage-requirement star — `Custom` requirements key on the field id, and
  // the checker falls back to the lead row for a deal, so the same mark is
  // right in both the Lead and the Deal preference sections.
  const required = useRequiredFieldMark("custom", field?.id);

  const selectedIds: string[] = useMemo(
    () =>
      Array.isArray(scope?.optionIds)
        ? scope.optionIds.map((id: unknown) => String(id))
        : [],
    [scope],
  );

  const options = useMemo(() => {
    const all = field?.options ?? [];
    const narrowed = resolvedOptions?.length
      ? all.filter((op: any) =>
          resolvedOptions.some((r) => Number(r.id) === Number(op.id)),
        )
      : all;
    return narrowed.map((op: any) => ({
      value: String(op.id),
      label: String(op.label ?? op.name ?? op.value ?? ""),
    }));
  }, [field, resolvedOptions]);

  // A field migrated from TEXT_FIELD to SELECTOR can still report the old
  // `type` briefly while its values already live on `optionIds` — same
  // guard `getCustomFieldValue` applies for display.
  // Number / Percentage / Date are never pickers — their `options` hold only
  // the hidden "Text" anchor.
  const isFormatted = isFormattedCfType(field?.type);
  const behavesAsSelector =
    field?.type === "SELECTOR" ||
    (!isFormatted && options.length > 0 && selectedIds.length > 0);

  const editor: InlineEditorSpec = behavesAsSelector
    ? isTagSelector(field)
      ? { kind: "multiselect", value: selectedIds, options }
      : { kind: "select", value: selectedIds[0] ?? "", options, clearable: true }
    : field?.type === "DATE"
      ? {
          kind: "date",
          value: isValidYmd(scope?.textValue ?? "") ? scope.textValue : "",
          maxDate: resolveCfDateMax(field?.dateMax),
          yearPicker: true,
          clearable: true,
        }
    : field?.type === "NUMBERS" || field?.type === "PERCENTAGE"
      ? {
          kind: "text",
          value: scope?.textValue ?? "",
          placeholder: field.type === "PERCENTAGE" ? "0 to 100" : "Add number",
          decimal: field.type === "NUMBERS" ? "signed" : "unsigned",
        }
    : field?.type === "REMARK"
      ? {
          kind: "textarea",
          value: scope?.textValue ?? "",
          placeholder: "Add a note…",
        }
      : {
          // The field name is already the label directly above the editor —
          // repeating it in the placeholder just overflows a one-line box.
          kind: "text",
          value: scope?.textValue ?? "",
          placeholder: "Add value",
        };

  const handleSave = (next: string | string[]) => {
    if (behavesAsSelector) {
      const optionIds = Array.isArray(next) ? next : next ? [next] : [];
      return onSave(Number(field.id), { optionIds });
    }
    return onSave(Number(field.id), { textValue: String(next ?? "") });
  };

  return (
    <InlineEditableDetail
      label={getCfDisplayName(field)}
      required={required}
      value={getCustomFieldValue(field, scopes) || "N/A"}
      readOnly={!canEdit}
      editor={editor}
      onSave={handleSave}
    />
  );
}
